import { useCallback, useEffect, useRef, useState } from 'react'
import { useApp } from './AppStore'
import { lastLiveEvent, tally } from '../domain/derive'
import { mergeChanges, mergeEvent } from '../domain/merge'
import { newId } from '../lib/id'
import type { Game, ID, StatEvent, StatTypeId } from '../domain/types'

export interface LiveGame {
  game: Game | null
  events: StatEvent[]
  counts: Map<string, number>
  loading: boolean
  /** Set when a write to storage failed, so the UI can warn instead of silently losing taps. */
  saveError: string | null
  /** Id of the event most recently recorded, for the tap flash animation. */
  lastEventId: ID | null

  record(playerId: ID, statType: StatTypeId): void
  undo(): void
  deleteEvent(eventId: ID): void
  reassignEvent(eventId: ID, playerId: ID): void
  setPeriod(period: number): void
  /** The server has acknowledged these versions; they leave the outbox. */
  markSynced(ids: ID[], at: number): void
  /** Fold in events recorded on another device, e.g. after taking over. */
  mergeRemote(incoming: StatEvent[]): Promise<void>
}

/**
 * Owns the in-progress game. Every mutation is applied optimistically to local
 * state and written to storage in the same tick -- there is no explicit save, so
 * killing the app mid-game loses at most the tap currently in flight.
 */
export function useLiveGame(gameId: ID | null): LiveGame {
  const { repo, games, players, saveGame } = useApp()
  const [events, setEvents] = useState<StatEvent[]>([])
  const [loading, setLoading] = useState(gameId != null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [lastEventId, setLastEventId] = useState<ID | null>(null)

  const game = gameId ? (games.find((g) => g.id === gameId) ?? null) : null
  const gameRef = useRef(game)
  gameRef.current = game

  useEffect(() => {
    if (!gameId) {
      setEvents([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    repo
      .listEventsByGame(gameId)
      .then((rows) => {
        if (cancelled) return
        setEvents(rows)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setSaveError(err instanceof Error ? err.message : String(err))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [gameId, repo])

  const playerProfileId = useCallback(
    (playerId: ID) => players.find((p) => p.id === playerId)?.playhqProfileId ?? null,
    [players],
  )

  const persist = useCallback(
    (event: StatEvent) => {
      repo.saveEvent(event).catch((err: unknown) => {
        setSaveError(err instanceof Error ? err.message : String(err))
      })
    },
    [repo],
  )

  const record = useCallback(
    (playerId: ID, statType: StatTypeId) => {
      const current = gameRef.current
      if (!current) return
      const now = Date.now()
      const event: StatEvent = {
        id: newId(),
        gameId: current.id,
        playerId,
        statType,
        period: current.currentPeriod,
        at: now,
        deleted: false,
        // External keys stored alongside the internal ones so an event survives
        // the backend rebuilding its rows on a re-sync.
        playhqGameId: current.playhqGameId ?? null,
        playhqProfileId: playerProfileId(playerId),
        createdAt: now,
        updatedAt: now,
      }
      setEvents((prev) => [...prev, event])
      setLastEventId(event.id)
      persist(event)
    },
    [persist, playerProfileId],
  )

  const softDelete = useCallback(
    (eventId: ID) => {
      setEvents((prev) => {
        const i = prev.findIndex((e) => e.id === eventId)
        if (i < 0) return prev
        const updated: StatEvent = { ...prev[i]!, deleted: true, updatedAt: Date.now() }
        persist(updated)
        const next = prev.slice()
        next[i] = updated
        return next
      })
    },
    [persist],
  )

  const undo = useCallback(() => {
    setEvents((prev) => {
      const target = lastLiveEvent(prev)
      if (!target) return prev
      const updated: StatEvent = { ...target, deleted: true, updatedAt: Date.now() }
      persist(updated)
      return prev.map((e) => (e.id === target.id ? updated : e))
    })
    setLastEventId(null)
  }, [persist])

  const reassignEvent = useCallback(
    (eventId: ID, playerId: ID) => {
      setEvents((prev) => {
        const i = prev.findIndex((e) => e.id === eventId)
        if (i < 0) return prev
        // The external key must follow the reassignment, or a later re-sync
        // would reattach this event to the wrong person.
        const updated: StatEvent = {
          ...prev[i]!,
          playerId,
          playhqProfileId: playerProfileId(playerId),
          updatedAt: Date.now(),
        }
        persist(updated)
        const next = prev.slice()
        next[i] = updated
        return next
      })
    },
    [persist, playerProfileId],
  )

  const markSynced = useCallback(
    (ids: ID[], at: number) => {
      const set = new Set(ids)
      setEvents((prev) => {
        const touched: StatEvent[] = []
        const next = prev.map((e) => {
          if (!set.has(e.id)) return e
          // Record the version that was acknowledged, not the moment of the
          // reply: a tap made while the push was in flight must stay queued.
          const updated = { ...e, syncedAt: e.updatedAt }
          touched.push(updated)
          return updated
        })
        if (touched.length > 0) {
          repo.saveEvents(touched).catch((err: unknown) => {
            setSaveError(err instanceof Error ? err.message : String(err))
          })
        }
        return next
      })
      void at
    },
    [repo],
  )

  /**
   * Merge another device's events into this one's.
   *
   * The same three rules the server merges by, because a device that has just
   * taken over holds both copies and has to reach the same answer the server
   * did: identity is the event id, a newer updatedAt wins, and `deleted` only
   * ever goes from false to true. An undo that came back resurrected would be a
   * number nobody tapped.
   */
  const mergeRemote = useCallback(
    async (incoming: StatEvent[]) => {
      if (incoming.length === 0) return
      const existing = await repo.listEventsByGame(incoming[0]!.gameId)
      const byId = new Map(existing.map((e) => [e.id, e]))
      const toWrite: StatEvent[] = []

      for (const remote of incoming) {
        const local = byId.get(remote.id)
        if (!local) {
          toWrite.push(remote)
          continue
        }
        const merged = mergeEvent(local, remote)
        if (mergeChanges(local, merged)) toWrite.push(merged)
      }

      if (toWrite.length > 0) await repo.saveEvents(toWrite)
      const fresh = await repo.listEventsByGame(incoming[0]!.gameId)
      setEvents(fresh)
    },
    [repo],
  )

  const setPeriod = useCallback(
    (period: number) => {
      const current = gameRef.current
      if (!current || current.currentPeriod === period) return
      void saveGame({ ...current, currentPeriod: period })
    },
    [saveGame],
  )

  return {
    game,
    events,
    counts: tally(events),
    loading,
    saveError,
    lastEventId,
    record,
    undo,
    deleteEvent: softDelete,
    reassignEvent,
    setPeriod,
    markSynced,
    mergeRemote,
  }
}
